/**
 * TicketTypes — this event's own ticket-type catalog, resolved once per
 * selected event so a raw typeId never reaches the operator's screen
 * — an operator reads a label, never an id: the wrong-type stop detail, the
 * pending-admit/already-in ticket sheet, the verdict splash's admit line,
 * and the settings page's type-filter chips all read a human name through
 * this map, never the id itself. Pure state — no Firestore import (the
 * io-in-view-or-state guard); each consumer mounts its own `.sync.ts`
 * writer, per this seam's established one-`.sync.ts`-per-consumer pattern —
 * `scanner`'s `ticket-types.sync.ts` writes it from a public,
 * unconditional read (TicketType's own rule: `allow read/list: if true`)
 * scoped to event.state.ts's `selectedEventId`, and the design studio's
 * `checkin.sync.ts` writes it from fixtures.
 *
 * The cell stores the parsed types and nothing derived from them: a label is
 * a function of the reader's own language, and this catalog is read by a
 * door phone and by the operator console at once, so a label frozen into the
 * cell by whichever writer happened to run would outlive the language the
 * reader has since chosen.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { type Locale, resolveLocalizedText } from "@momo/i18n";
import type { TicketType } from "@momo/types";
import { uiLanguage } from "../../i18n/localize.ts";

export type TicketTypeOption = {
	readonly id: string;
	readonly name: string;
};

export type TicketTypesState = {
	readonly byId: ReadonlyMap<string, TicketType.TicketType>;
	/** Which event these types were read for — `null` while no catalog has
	 *  answered: no event scopes it, or a switch of organization or event has
	 *  not been answered yet. `null` is this cell's unanswered state; an
	 *  answered empty catalog carries its event id. Every writer stamps it in
	 *  the same map that builds `byId`, because the two cannot be allowed to
	 *  disagree. An id is a Firestore auto-id meaningful only inside the event
	 *  that minted it, so a reader that narrows a door by these ids without
	 *  asking which event they belong to builds an allow-list naming nothing
	 *  in the event now selected, and refuses every guest holding a
	 *  correctly-issued ticket. */
	readonly eventId: string | null;
};

// keepOnClear: this catalog is public and identity-free (its own `allow
// read/list: if true`), and its listener is scoped to `selectedEventId` —
// itself kept across an identity change (event.state.ts), so nothing re-runs
// that listener on a clearStates() reset. Wiping it would leave every surface
// that resolves a typeId into a name showing nothing at all.
const initial: TicketTypesState = { byId: new Map(), eventId: null };
export const ticketTypesIndex = state("scanner/ticket-types").memory(initial, {
	keepOnClear: true,
});

/** The type's own name in `locale`, or `null` — never the id itself — when
 *  the selected event's catalog hasn't loaded yet or carries no entry for
 *  `typeId`. The locale is a parameter rather than a read here: this module
 *  is pure, and every caller already holds the language of the sentence this
 *  name is read inside. */
export function ticketTypeName(typeId: string, locale: Locale): string | null {
	const type = ticketTypesIndex.value.byId.get(typeId);
	if (!type) return null;
	return resolveLocalizedText(type.nameByLocale, type.name, locale);
}

/** `ticketTypeName` for the whole catalog: every type the selected event
 *  offers, named in the reader's own language, ordered by that name. Sorted
 *  on what the operator reads rather than on the source string behind it,
 *  since a list ordered by a language nobody on this screen is reading looks
 *  unsorted. Distinct from manual-lookup.state.ts's `ticketTypeOptions`,
 *  which narrows to the types the loaded guests actually hold and returns
 *  bare ids.
 *
 *  A computed, and the language a read rather than a parameter: the settings
 *  panel that renders these chips re-renders on every filter toggle it owns,
 *  and a function would re-map and re-sort the whole catalog on each of them.
 *  `uiLanguage()` reads the localization engine's own signal, so the
 *  projection re-runs when the language flips and on nothing else. */
export const ticketTypeNames = computed<readonly TicketTypeOption[]>(() => {
	const locale = uiLanguage();
	return [...ticketTypesIndex.value.byId.entries()]
		.map(([id, type]) => ({
			id,
			name: resolveLocalizedText(type.nameByLocale, type.name, locale),
		}))
		.sort((a, b) => a.name.localeCompare(b.name));
});
